import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Check, ChevronDown } from 'lucide-react';
import { formatTime, shortDate, shortDay } from '@utils/i18n';
import { cn } from '@utils/cn';
import { ExternalDateBarProps } from './types';

export const ExternalDateBar = ({ date, time, confirmed, related }: ExternalDateBarProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'scheduling.external' });
  const [open, setOpen] = useState(false);
  const count = related.length;

  return (
    <div className="relative z-10 shrink-0 bg-[#f8f9fd]">
      <button
        type="button"
        aria-expanded={open}
        disabled={!count}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-12 w-full items-center gap-2 border-b border-neutral-100 bg-[#f8f9fd] px-4 text-left shadow-[0_4px_10px_rgba(20,30,45,.04)]">
        <CalendarDays
          className={cn('h-5 w-5 shrink-0', date ? 'text-neutral-500' : 'text-neutral-400')}
        />
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5 truncate tabular-nums">
          <b className={cn('text-base', date ? 'text-neutral-800' : 'text-neutral-500')}>
            {date ? `${shortDay(date)}, ${shortDate(date)}` : t('notScheduled')}
          </b>
          {date && (
            <span className="text-[15px] text-neutral-600">
              · {time ? formatTime(time) : t('noTime')}
            </span>
          )}
        </span>
        {confirmed && (
          <span className="flex h-[21px] shrink-0 items-center gap-0.5 rounded-full bg-success-50 px-2 text-[11px] font-bold text-success-700">
            <Check className="h-3 w-3" strokeWidth={3} />
            {t('confirmed')}
          </span>
        )}
        {count > 0 && (
          <>
            <span className="shrink-0 text-xs font-semibold text-blue-500">
              {t('related', { count })}
            </span>
            <ChevronDown
              className={cn(
                'h-[18px] w-[18px] shrink-0 transition-transform',
                open && 'rotate-180',
              )}
            />
          </>
        )}
      </button>
      {open && count > 0 && (
        <div className="border-b border-neutral-100 bg-[#f8f9fd] shadow-[0_6px_12px_rgba(20,30,45,.06)]">
          <div className="flex items-center gap-2 px-4 pb-1 pt-2.5">
            <span className="text-sm font-bold text-neutral-800">{t('relatedTitle')}</span>
            <span className="text-xs text-neutral-500">{t('schedules', { count })}</span>
          </div>
          <div className="flex max-h-[220px] flex-col items-start gap-1.5 overflow-y-auto px-4 pb-3 pt-1 [&>span]:max-w-full [&>span]:whitespace-normal [&>span]:text-left">
            {related}
          </div>
        </div>
      )}
    </div>
  );
};
